import React from "react";
import { useEffect, useReducer } from "react";
import { useTranslation } from "react-i18next";
import { IVAT } from "../../../interfaces/IVat";
import ICategory from "../categories/ICategory";
import ISubCategory from "../categories/ISubCategory";
import IMenuItemBody from "../../../interfaces/IMenuItemBody";
import { ProductModifyDialog } from "./ProductModifyDialog";
import tabs from "./ProductTabs";
import { ProductListControls } from "./productList/ProductListControls";
import { setCategoryId, setItemsNotInMenu, setMenusList, setProductList, setSelectedProductList, setSelectedSubcategories, setSubcategoryId } from "./state/itemContainer.actions";
import { productListInitalState, productListReducer } from "./state/itemContainer.reducer";
import { IProduct } from "../../../interfaces/IProduct";
import { ItemProductListView } from "./ItemProductListView";
import { ICallAPIFunction } from "../../../yoello-lib/modules/API2/types";
import useParseTabItems from './useParseTabItems';
import { onApplyFilters } from "./utils";
import { IMenuData } from "../../manageMenu/types";
import { useProductsData } from "./useProductsData";
import APIDefintions from '../../../yoello-lib/modules/API2/APIDefs';
import { Route, Switch, useHistory, useRouteMatch } from "react-router-dom";
import { DialogRoute } from "../../dialogRoutes/DialogRoute";
import { AddItemsToMenuDialog } from "../../manageMenu/AddToMenuInBulk/AddItemsToMenuDialog";
import Styles from "../../manageMenu/manageMenu.module.scss";
import { ADD_ITEM, BY_ID, CREATE_URL, MENU_URL } from "../../../const/URL.const";
import { ISidebarItem } from "../../Routes";
import { useSideTabs } from "../../../hooks/useSideTabs";

interface IProductsListProps {
    categories: ICategory[];
    subcategories: ISubCategory[];
    menuItems: IMenuItemBody[];
    venueId: string;
    menuItemAPICall: menuItemAPICall;
    vat: IVAT[];
    isLoading: boolean;
    updateMenuItemPosition: (positions: string[]) => void;
    tags: IItemTags[];
    manageMenuAPI: manageMenuAPICall;
    tab: ISidebarItem;

}
export type menuItemAPICall = ICallAPIFunction<IMenuItemBody | IMenuItemBody[]>;
export type manageMenuAPICall = ICallAPIFunction<IMenuData | IMenuData[]>;
export enum ProductType{
    MainItem = "MainItem",
    ExtraItem = "ExtraItem"
}

export interface IProductListState {
    /**The list of all the menu items sorted into a useable list */
    productList: IProduct[];
    /**The selected sub category list */
    selectedProductList: IMenuItemBody[];
    selectedSubCategories: ISubCategory[];
    categoryID: string;
    subcategoryID: string;
    itemsNotInMenu?: IMenuItemBody[];
    menus?: IMenuData[];
    filterState: any[];

}
export const newProductTemplate: IProduct =
{
    details:
    {
        tags: [],
        extra_tags: { newTags: [], oldTags: [] },
        preference_tags: { newTags: [], oldTags: [] },
        collection_enabled: true,
        delivery_enabled: true,
        table_service_enabled: true,
        menus: []
    },
    categoryDisplayName: "NOT SET",
    category: 0,
    isNewItem: true
};
export function ItemContainer({
    categories,
    subcategories,
    menuItems,
    venueId,
    menuItemAPICall,
    vat,
    isLoading,
    updateMenuItemPosition,
    tags,
    manageMenuAPI,
    tab
}: IProductsListProps) {
    const { t } = useTranslation();
    const { parseCategoryItems, parseSubcategoryItems } = useParseTabItems();
    const { defaultCategoryTab, categoriesLoaded } = parseCategoryItems(categories, subcategories);
    let { path } = useRouteMatch();
    useSideTabs(tab);
    const [state, dispatch] = useReducer(
        productListReducer,
        productListInitalState
    );
    const {
        productList,
        selectedProductList,
        categoryID,
        subcategoryID,
        filterState,
        selectedSubCategories,
        itemsNotInMenu,
        menus
    } = state;
    const { defaultSubcategoryTab, subcategoriesLoaded } = parseSubcategoryItems(subcategories, categoryID);

    /**Orphaned Menu Item(Not within a menu) data */
    const { callAPI: orphanedMenuItemsAPI } = useProductsData<IMenuItemBody>(
        APIDefintions.menusItems, venueId, (item) => dispatch(setItemsNotInMenu(item)), { 'orphaned': 'true', 'sold_as_extra_only': "false" });

    // eslint-disable-next-line
    const { callAPI: updateMenusListAPI } = useProductsData<IMenuData>(
        APIDefintions.menus, venueId, (item) => dispatch(setMenusList(item)));


    const history = useHistory();
    useEffect(() => {
        if (categories && categories.length > 0) {
            dispatch(setCategoryId(defaultCategoryTab));
        }
        //eslint-disable-next-line
    }, [categories, defaultCategoryTab]);

    useEffect(() => {
        const products = menuItemMap(menuItems, subcategories, categories);
        dispatch(setProductList(products));
        //eslint-disable-next-line
    }, [categories, subcategories, menuItems]);

    useEffect(() => {
        let selectedList: IMenuItemBody[] = [];
        if (subcategoryID.length > 0) {
            selectedList = productList
                .filter((products: IProduct) => products.details.subcategory_id === subcategoryID)
                .map((product: IProduct) => {
                    const hasPrice = (product.details && product.details.pricing_options && Object.keys(product.details.pricing_options).length > 0);
                    let price = 0;
                    if (hasPrice) {
                        const key = Object.keys(product.details.pricing_options)[0];
                        price = product.details.pricing_options[key]?.option_price;
                    }
                    if (product.details.sold_as_extra_only && product.details.pricing_options_extras) {
                        const key = Object.keys(product.details.pricing_options_extras)[0];
                        price = product.details.pricing_options_extras[key]?.option_price;
                    }
                    if (!hasPrice) {
                        console.warn(`No price found for product!:`);
                        console.dir(product);
                    }
                    return { ...product.details, price };
                }).sort((a: any, b: any) => a.sort_position - b.sort_position);
            /**if we have any filters set then apply to the list of remaining items */
            if (filterState.length > 0) {
                const selectedFilters = {
                    inStock: filterState[0].values.includes("inStock"),
                    outStock: filterState[0].values.includes("outStock")
                }
                const allFilters = getFilters();
                selectedList = onApplyFilters(selectedList, selectedFilters, allFilters);
            }
        }
        dispatch(setSelectedProductList(selectedList));
        //eslint-disable-next-line
    }, [productList, subcategoryID, filterState]);

    useEffect(() => {
        if (categories?.length && subcategories?.length) {
            const filtered = subcategories?.filter((item) => item.category_id === categoryID);
            dispatch(setSelectedSubcategories(filtered));
        }
    }, [categories, subcategories, categoryID]);

    useEffect(() => {
        if (selectedSubCategories?.length) {
            dispatch(setSubcategoryId(defaultSubcategoryTab));
        } else {
            dispatch(setSubcategoryId('NONE'));
        }
        //eslint-disable-next-line
    }, [selectedSubCategories]);

    function switchCategory(id: string) {
        const cat = subcategories.filter(category => category.id === id)[0];
        dispatch(setCategoryId(cat?.category_id || ""));
        setTimeout(() => {
            dispatch(setSubcategoryId(id));
        }, 0);
    }
    function getFilters() {
        const filters: {
            key: string,
            values: string[]
        } | undefined = filterState[0];
        return [
            {
                key: "stock",
                label: "filters.stock",
                filters: [
                    {
                        key: "inStock",
                        label: "filters.inStock",
                        param: "enabled",
                        checked: filters ? filters.values.includes("inStock") : false,
                        expected: true
                    },
                    {
                        key: "outStock",
                        label: "filters.outStock",
                        param: "enabled",
                        checked: filters ? filters.values.includes("outStock") : false,
                        expected: false
                    },
                ],
            }
        ]
    }
    function onItemSelection(product: IProduct | IMenuItemBody) {
        const item = product.details ? product.details : product;
        history.push(`${path}/${item?.id ?? CREATE_URL}`, {
            item
        })
    }

    return <Switch>
        <Route path={path}>

            <ProductListControls updateMenusListAPI={updateMenusListAPI} menusList={menus} menuItems={menuItems}
                orphanedMenuItemsAPI={orphanedMenuItemsAPI} manageMenuAPI={manageMenuAPI} menuItemsNotInMenu={itemsNotInMenu} subcategories={subcategories}
                isLoading={isLoading} dispatch={dispatch} {...state}
                categories={categories} onItemSelection={onItemSelection} venueId={venueId} filtersList={getFilters()} />
            <ItemProductListView productList={selectedProductList} subcategories={subcategories}
                onItemSelection={onItemSelection} menuItemAPICall={menuItemAPICall} onSortedItem={updateMenuItemPosition}
                venueId={venueId} isLoading={isLoading || !categoriesLoaded || !subcategoriesLoaded} />
            <Route exact path={`${path}${BY_ID}`}>
                <DialogRoute Component={ProductModifyDialog}
                    navigateBackRoute={path}
                    ComponentPropsFunctions={{
                        subcategories: subcategories || [],
                        menuItemAPICall,
                        vat,
                        switchCategory,
                        tags,
                        manageMenuAPI,
                        orphanedMenuItemsAPI,
                    }}
                    dialogTitle={t("forms.modify.title")} />
            </Route>
            {itemsNotInMenu.length > 0 &&
                <Route exact path={`${path}${MENU_URL}${ADD_ITEM}`}>
                    <DialogRoute Component={AddItemsToMenuDialog}
                        navigateBackRoute={path}
                        ComponentPropsFunctions={{
                            subcategories: subcategories || [],
                            menuItemAPICall,
                            manageMenuAPI,
                            orphanedMenuItemsAPI: orphanedMenuItemsAPI,
                            menuItems: itemsNotInMenu,
                        }}
                        dialogTitle={t("manageMenu.manageMenuContainer.AddItemsToMenu")}
                        dialogClassName={Styles.AddItemsToMenuDialogContainer}
                    />
                </Route>
            }
        </Route>
    </Switch>;
}



function menuItemMap(menuItems: IMenuItemBody[],
    subcategories: ISubCategory[],
    categories: ICategory[]): IProduct[] {
    return menuItems.map((menuItem) => {
        const filteredCats = subcategories.filter((cat) => cat.id === menuItem.subcategory_id);
        const category = (filteredCats.length > 0) ? filteredCats[0] : null;
        const abv = menuItem.abv ? menuItem.abv / 10 : undefined;
        let categoryId;
        try {
            categoryId = resolveCategory(categories, category ? category.category_id : "Not Set");
        } catch (error) {
            console.log("Missing category ids", menuItem.name_public);
            categoryId = undefined;
        }
        return {
            details: { ...menuItem, abv },
            categoryDisplayName: category ? category.subcategory_name : "ERROR: NO CATEGORY FOUND",
            category: categoryId,
            isNewItem: false
        };
    });
}

/**
 * Work out which tab (Food/Misc/Drinks) to show the item under
 * We could have mapped this, but keeping a separation between UI and DB terms
 */
function resolveCategory(categories: ICategory[], categoryID: string): tabs {
    const matches = categories.filter((cat: ICategory) => cat.id === categoryID);
    if (matches.length === 0) { throw Error(`Failed to find category "${categoryID}"`); }
    const category = matches[0];
    switch (category.category_name) {
        case "FOOD":
            return tabs.Food;
        case "DRINK":
            return tabs.Drinks;
        case "MISC":
        default:
            return tabs.Food;
    }
}